import React, { useState } from "react";
import styled from "styled-components";
import { MdContentCopy, MdVisibility, MdVisibilityOff } from "react-icons/md";

export default function CredentialModal({ showModal, setShowModal, credentials }) {
  const [accessCode, setAccessCode] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState("");
  
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!showModal || !credentials) return null;

  const handleUnlock = () => {
    if (accessCode === "ct-access") {
      setIsUnlocked(true);
      setError("");
    } else {
      setError("Invalid access code");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const closeAndReset = () => {
    setShowModal(false);
    setTimeout(() => {
      setAccessCode("");
      setIsUnlocked(false);
      setError("");
      setShowUsername(false);
      setShowPassword(false);
    }, 300);
  };

  return (
    <ModalOverlay onClick={closeAndReset}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h4 className="font20 extraBold">Demo Credentials</h4>
          <CloseButton onClick={closeAndReset}>&times;</CloseButton>
        </ModalHeader>
        
        {!isUnlocked ? (
          <ModalBody>
            <p className="font13" style={{ marginBottom: "15px", color: "#555" }}>
              Please enter the access code to view the credentials for this demo.
            </p>
            <InputGroup>
              <AccessInput 
                type="password" 
                placeholder="Enter access code" 
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
              />
              <UnlockButton onClick={handleUnlock}>Unlock</UnlockButton>
            </InputGroup>
            {error && <ErrorText className="font13">{error}</ErrorText>}
          </ModalBody>
        ) : (
          <ModalBody>
            <CredentialRow>
              <ContentCol>
                <Label className="font12">Username</Label>
                <ValueRow>
                  <Value className="font15 semiBold">
                    {showUsername ? credentials.username : "••••••••••••"}
                  </Value>
                </ValueRow>
              </ContentCol>
              <ActionCol>
                <IconBtn onClick={() => setShowUsername(!showUsername)} title="Toggle Visibility">
                  {showUsername ? <MdVisibilityOff /> : <MdVisibility />}
                </IconBtn>
                <CopyBtn onClick={() => copyToClipboard(credentials.username)} title="Copy Username">
                  <MdContentCopy />
                </CopyBtn>
              </ActionCol>
            </CredentialRow>
            
            <CredentialRow>
              <ContentCol>
                <Label className="font12">Password</Label>
                <ValueRow>
                  <Value className="font15 semiBold">
                    {showPassword ? credentials.password : "••••••••••••"}
                  </Value>
                </ValueRow>
              </ContentCol>
              <ActionCol>
                <IconBtn onClick={() => setShowPassword(!showPassword)} title="Toggle Visibility">
                  {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </IconBtn>
                <CopyBtn onClick={() => copyToClipboard(credentials.password)} title="Copy Password">
                  <MdContentCopy />
                </CopyBtn>
              </ActionCol>
            </CredentialRow>
            
            <div style={{ marginTop: "15px", textAlign: "center" }}>
              <p className="font13" style={{ color: "#16a34a" }}>
                Credentials unlocked! You can now copy them and proceed to the demo.
              </p>
            </div>
          </ModalBody>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;
const ModalContent = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #9ca3af;
  line-height: 1;
  
  &:hover { color: #111827; }
`;
const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;
const InputGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;
const AccessInput = styled.input`
  flex-grow: 1;
  padding: 10px 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #7620ff;
    box-shadow: 0 0 0 2px rgba(118, 32, 255, 0.2);
  }
`;
const UnlockButton = styled.button`
  background-color: #111827;
  color: white;
  border: none;
  padding: 0 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;

  &:hover { background-color: #374151; }
`;
const ErrorText = styled.p`
  color: #ef4444;
  margin: 0;
`;
const CredentialRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f9fafb;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
`;
const ContentCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  overflow: hidden;
`;
const ActionCol = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Label = styled.div`
  color: #6b7280;
  margin-bottom: 4px;
  text-align: left;
`;
const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const Value = styled.div`
  color: #111827;
  font-family: monospace;
  font-size: 16px;
  letter-spacing: 1px;
`;
const IconBtn = styled.button`
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  font-size: 20px;

  &:hover {
    color: #4b5563;
  }
`;
const CopyBtn = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
`;
